import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiDelete, apiGet, apiLogin, apiPatch, apiPost, apiPut, clearToken, getToken } from '../api';

function jsonStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return '';
  }
}

function parseJson(text) {
  if (!text || !String(text).trim()) return { ok: true, value: {} };
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (err) {
    return { ok: false, error: err };
  }
}

function AdminPanel() {
  const [token, setTokenState] = useState(() => getToken());
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [activeTab, setActiveTab] = useState('appointments');

  const headers = useMemo(() => ({}), []);

  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [appointments, setAppointments] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clinicProfile, setClinicProfile] = useState(null);

  const [selectedId, setSelectedId] = useState('');
  const [payloadText, setPayloadText] = useState('{}');

  const loadAppointments = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/appointments/requests', { headers });
      setAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load appointment requests');
    } finally {
      setBusy(false);
    }
  }, [headers]);

  const loadInquiries = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/contact/inquiries', { headers });
      setInquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load inquiries');
    } finally {
      setBusy(false);
    }
  }, [headers]);

  const loadAdminUsers = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/admin-users', { headers });
      setAdminUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load admin users');
    } finally {
      setBusy(false);
    }
  }, [headers]);

  const loadFeedback = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/feedback/admin', { headers });
      setFeedback(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load feedback');
    } finally {
      setBusy(false);
    }
  }, [headers]);

  const loadPrograms = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/content/admin/programs', { headers });
      const items = data && typeof data === 'object' && Array.isArray(data.items) ? data.items : [];
      setPrograms(items);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load programs');
    } finally {
      setBusy(false);
    }
  }, [headers]);

  const loadTestimonials = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/content/admin/testimonials', { headers });
      const items = data && typeof data === 'object' && Array.isArray(data.items) ? data.items : [];
      setTestimonials(items);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load testimonials');
    } finally {
      setBusy(false);
    }
  }, [headers]);

  const loadClinicProfile = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const data = await apiGet('/content/admin/clinic-profile', { headers });
      setClinicProfile(data || null);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load clinic profile');
    } finally {
      setBusy(false);
    }
  }, [headers]);

  useEffect(() => {
    setSelectedId('');
    setPayloadText('{}');
  }, [activeTab]);

  function requireTokenOrLogout() {
    const fresh = getToken();
    if (fresh) {
      if (fresh !== token) setTokenState(fresh);
      return true;
    }
    clearToken();
    if (token) setTokenState('');
    setError('');
    return false;
  }

  const refresh = useCallback(async () => {
    if (!requireTokenOrLogout()) return;

    if (activeTab === 'appointments') return loadAppointments();
    if (activeTab === 'inquiries') return loadInquiries();
    if (activeTab === 'adminUsers') return loadAdminUsers();
    if (activeTab === 'feedback') return loadFeedback();
    if (activeTab === 'programs') return loadPrograms();
    if (activeTab === 'testimonials') return loadTestimonials();
    if (activeTab === 'clinicProfile') return loadClinicProfile();
  }, [activeTab, token, loadAdminUsers, loadAppointments, loadClinicProfile, loadFeedback, loadInquiries, loadPrograms, loadTestimonials]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const items = useMemo(() => {
    if (activeTab === 'appointments') return appointments;
    if (activeTab === 'inquiries') return inquiries;
    if (activeTab === 'adminUsers') return adminUsers;
    if (activeTab === 'feedback') return feedback;
    if (activeTab === 'programs') return programs;
    if (activeTab === 'testimonials') return testimonials;
    return [];
  }, [activeTab, adminUsers, appointments, feedback, inquiries, programs, testimonials]);

  const selectedItem = useMemo(() => {
    if (!selectedId) return null;
    return items.find((it) => String(it && it.id) === String(selectedId)) || null;
  }, [items, selectedId]);

  useEffect(() => {
    if (selectedItem) {
      setPayloadText(jsonStringify(selectedItem));
    }
  }, [selectedItem]);

  async function handlePatch() {
    if (!requireTokenOrLogout()) return;
    if (!selectedId) {
      setError('Select an item first');
      return;
    }

    const parsed = parseJson(payloadText);
    if (!parsed.ok) {
      setError('Invalid JSON');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'appointments') {
        await apiPatch(`/appointments/requests/${selectedId}`, parsed.value, { headers });
        await loadAppointments();
      } else if (activeTab === 'inquiries') {
        await apiPatch(`/contact/inquiries/${selectedId}`, parsed.value, { headers });
        await loadInquiries();
      } else if (activeTab === 'adminUsers') {
        await apiPatch(`/admin-users/${selectedId}`, parsed.value, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'feedback') {
        await apiPatch(`/feedback/${selectedId}`, parsed.value, { headers });
        await loadFeedback();
      } else if (activeTab === 'programs') {
        await apiPatch(`/content/admin/programs/${selectedId}`, parsed.value, { headers });
        await loadPrograms();
      } else if (activeTab === 'testimonials') {
        await apiPatch(`/content/admin/testimonials/${selectedId}`, parsed.value, { headers });
        await loadTestimonials();
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!requireTokenOrLogout()) return;
    if (!selectedId) {
      setError('Select an item first');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'appointments') {
        await apiDelete(`/appointments/requests/${selectedId}`, { headers });
        await loadAppointments();
      } else if (activeTab === 'inquiries') {
        await apiDelete(`/contact/inquiries/${selectedId}`, { headers });
        await loadInquiries();
      } else if (activeTab === 'adminUsers') {
        await apiDelete(`/admin-users/${selectedId}`, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'feedback') {
        await apiDelete(`/feedback/${selectedId}`, { headers });
        await loadFeedback();
      } else if (activeTab === 'programs') {
        await apiDelete(`/content/admin/programs/${selectedId}`, { headers });
        await loadPrograms();
      } else if (activeTab === 'testimonials') {
        await apiDelete(`/content/admin/testimonials/${selectedId}`, { headers });
        await loadTestimonials();
      }
      setSelectedId('');
    } catch (err) {
      setError(err && err.message ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleCreate() {
    if (!requireTokenOrLogout()) return;

    const parsed = parseJson(payloadText);
    if (!parsed.ok) {
      setError('Invalid JSON');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (activeTab === 'adminUsers') {
        await apiPost('/admin-users', parsed.value, { headers });
        await loadAdminUsers();
      } else if (activeTab === 'inquiries') {
        await apiPost('/contact/inquiries', parsed.value, { headers });
        await loadInquiries();
      } else if (activeTab === 'programs') {
        await apiPost('/content/admin/programs', parsed.value, { headers });
        await loadPrograms();
      } else if (activeTab === 'testimonials') {
        await apiPost('/content/admin/testimonials', parsed.value, { headers });
        await loadTestimonials();
      } else {
        setError('Create not supported for this section');
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleClinicProfileSave(method) {
    if (!requireTokenOrLogout()) return;

    const parsed = parseJson(payloadText);
    if (!parsed.ok) {
      setError('Invalid JSON');
      return;
    }

    setError('');
    setBusy(true);
    try {
      if (method === 'PUT') {
        await apiPut('/content/admin/clinic-profile', parsed.value, { headers });
      } else {
        await apiPatch('/content/admin/clinic-profile', parsed.value, { headers });
      }
      await loadClinicProfile();
    } catch (err) {
      setError(err && err.message ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleClinicProfileDelete() {
    if (!requireTokenOrLogout()) return;

    setError('');
    setBusy(true);
    try {
      await apiDelete('/content/admin/clinic-profile', { headers });
      await loadClinicProfile();
    } catch (err) {
      setError(err && err.message ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  function onLogin() {
    const email = String(loginEmail || '').trim();
    const password = String(loginPassword || '');
    if (!email || !password) {
      setError('Enter email and password');
      return;
    }

    setError('');
    setBusy(true);
    apiLogin(email, password)
      .then(() => {
        setTokenState(getToken());
        setLoginPassword('');
      })
      .catch((err) => setError(err && err.message ? err.message : 'Login failed'))
      .finally(() => setBusy(false));
  }

  function onLogout() {
    clearToken();
    setTokenState('');
    setLoginPassword('');
    setSelectedId('');
    setPayloadText('{}');
    setError('');
  }

  function renderTabs() {
    const tabs = [
      { id: 'appointments', label: 'Appointment Requests' },
      { id: 'inquiries', label: 'Contact Inquiries' },
      { id: 'adminUsers', label: 'Admin Users' },
      { id: 'feedback', label: 'Feedback' },
      { id: 'clinicProfile', label: 'Clinic Profile' },
      { id: 'programs', label: 'Programs' },
      { id: 'testimonials', label: 'Testimonials' },
    ];

    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #ddd',
              background: activeTab === t.id ? '#111' : '#fff',
              color: activeTab === t.id ? '#fff' : '#111',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
    );
  }

  function renderList() {
    if (activeTab === 'clinicProfile') {
      return (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => loadClinicProfile()} disabled={busy}>
              Refresh
            </button>
            <button type="button" onClick={() => handleClinicProfileSave('PUT')} disabled={busy}>
              Save (PUT)
            </button>
            <button type="button" onClick={() => handleClinicProfileSave('PATCH')} disabled={busy}>
              Save (PATCH)
            </button>
            <button type="button" onClick={handleClinicProfileDelete} disabled={busy}>
              Delete
            </button>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontWeight: 700 }}>Clinic Profile JSON</div>
            <textarea
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              rows={18}
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
            <div style={{ color: '#666' }}>
              Current value loaded from server:
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', background: '#f6f6f6', padding: 10, borderRadius: 8 }}>
              {jsonStringify(clinicProfile)}
            </pre>
          </div>
        </div>
      );
    }

    const title =
      activeTab === 'appointments'
        ? 'Appointment Requests'
        : activeTab === 'inquiries'
          ? 'Contact Inquiries'
          : activeTab === 'adminUsers'
            ? 'Admin Users'
            : activeTab === 'feedback'
              ? 'Feedback'
              : activeTab === 'programs'
                ? 'Programs'
                : 'Testimonials';

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 12 }}>
        <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{title}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <button type="button" onClick={refresh} disabled={busy}>
              Refresh
            </button>
            <button type="button" onClick={() => setPayloadText('{}')} disabled={busy}>
              New JSON
            </button>
          </div>

          <div style={{ maxHeight: 420, overflow: 'auto', display: 'grid', gap: 6 }}>
            {items.map((it) => {
              const id = String(it && it.id ? it.id : '');
              const subtitle =
                activeTab === 'appointments'
                  ? `${it.status || 'new'} • ${it.name || ''}`
                  : activeTab === 'inquiries'
                    ? `${it.status || 'new'} • ${it.name || ''}`
                    : activeTab === 'adminUsers'
                      ? `${it.role || ''} • ${it.email || ''}`
                      : activeTab === 'feedback'
                        ? `${it.status || ''} • ${it.name || ''} (${it.rating || ''})`
                        : activeTab === 'programs'
                          ? `${it.slug || ''} • ${it.title || ''}`
                          : `${it.patientDisplayName || ''} • ${it.category || ''}`;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedId(id)}
                  style={{
                    textAlign: 'left',
                    borderRadius: 10,
                    border: selectedId === id ? '2px solid #111' : '1px solid #ddd',
                    padding: 10,
                    background: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 12, color: '#666' }}>{id}</div>
                  <div style={{ fontWeight: 700 }}>{subtitle}</div>
                </button>
              );
            })}

            {!items.length ? <div style={{ color: '#666' }}>No data</div> : null}
          </div>
        </div>

        <div style={{ border: '1px solid #eee', borderRadius: 10, padding: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700 }}>Editor</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={handleCreate} disabled={busy}>
                Create
              </button>
              <button type="button" onClick={handlePatch} disabled={busy}>
                Update (PATCH)
              </button>
              <button type="button" onClick={handleDelete} disabled={busy}>
                Delete
              </button>
            </div>
          </div>

          <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
            <div style={{ color: '#666' }}>Selected ID: {selectedId || '(none)'}</div>
            <textarea
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              rows={22}
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>Admin Panel</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a href="#" style={{ color: '#111', textDecoration: 'none', border: '1px solid #ddd', padding: '8px 10px', borderRadius: 8 }}>
            Public Site
          </a>
          {token ? (
            <button type="button" onClick={onLogout}>
              Logout
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
        {!token ? (
          <div style={{ display: 'grid', gap: 10, maxWidth: 520 }}>
            <div style={{ display: 'grid', gap: 6 }}>
              <label style={{ fontWeight: 700 }}>Email</label>
              <input
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="admin@example.com"
                style={{ padding: 10, borderRadius: 10, border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              <label style={{ fontWeight: 700 }}>Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Password"
                style={{ padding: 10, borderRadius: 10, border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" onClick={onLogin} disabled={busy}>
                {busy ? 'Signing in…' : 'Login'}
              </button>
              {error ? <div style={{ color: '#b00020', fontWeight: 700 }}>{error}</div> : null}
            </div>
          </div>
        ) : (
          <>
            {renderTabs()}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" onClick={refresh} disabled={busy}>
                {busy ? 'Loading…' : 'Refresh Current Tab'}
              </button>
              {error ? <div style={{ color: '#b00020', fontWeight: 700 }}>{error}</div> : null}
            </div>

            {renderList()}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
